import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import Gallery from "../components/Gallery";
import { getImagesByCategory } from "../data/imageMetadata";
import imageService from "../services/imageService";

const PageContainer = styled.div`
  padding: 2rem;
`;

const PageTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  font-size: 2.0rem;
  color: #2e2e2e;
  font-weight: bold;
`;

const FilterButtons = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  position: relative;

  button {
    margin: 0 0.5rem;
    padding: 0.5rem 1.5rem;
    font-size: 1rem;
    color: #2e2e2e;
    background: #f5f5f5;
    border: none;
    border-radius: 20px;
    cursor: pointer;
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    &:hover {
      background: #e0e0e0;
      box-shadow: 0px 6px 8px rgba(0, 0, 0, 0.15);
    }

    &:active {
      background: #2e2e2e;
      color: white;
      box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.2);
    }

    &.active {
      background: #2e2e2e;
      color: white;
      box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.2);
    }

    &:disabled {
      background: #ccc;
      color: #999;
      cursor: not-allowed;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: #666;
  font-size: 1.1rem;
`;

const LoadingProgress = styled.div`
  width: 100%;
  max-width: 300px;
  height: 4px;
  background-color: #f0f0f0;
  border-radius: 2px;
  margin: 1rem 0;
  overflow: hidden;
`;

const LoadingBar = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #2e2e2e, #666);
  border-radius: 2px;
  transition: width 0.3s ease;
  width: ${(props) => props.progress}%;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #d32f2f;
  background-color: #ffebee;
  border-radius: 8px;
  margin: 1rem 0;
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 2rem auto;
  padding: 1rem 2rem;
  font-size: 1rem;
  color: white;
  background: #2e2e2e;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #444;
    transform: translateY(-2px);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #666;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
`;

function GalleryPage() {
	const [selectedFilter, setSelectedFilter] = useState("all");
	const [images, setImages] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [loadingProgress, setLoadingProgress] = useState(0);
	const [visibleCount, setVisibleCount] = useState(12);
	const [loadingMore, setLoadingMore] = useState(false);

	// Get filtered image metadata
	const getFilteredMetadata = useCallback((category) => {
		return getImagesByCategory(category);
	}, []);

	// Load images with progress tracking
	const loadImages = useCallback(
		async (category, count = 12) => {
			try {
				setLoading(true);
				setError(null);
				setLoadingProgress(0);

				const metadata = getFilteredMetadata(category);
				const imagesToLoad = metadata.slice(0, count);

				if (imagesToLoad.length === 0) {
					setImages([]);
					setLoading(false);
					return;
				}

				// Load images with progress tracking
				const loadedImages = [];
				const batchSize = 3;

				for (let i = 0; i < imagesToLoad.length; i += batchSize) {
					const batch = imagesToLoad.slice(i, i + batchSize);
					const batchPromises = batch.map(async (imageData) => {
						try {
							const result = await imageService.getImageWithMetadata(imageData);
							return {
								...result,
								// Ensure we have the required properties for Gallery component
								id: result.id || imageData.id,
								description: result.description || imageData.description || "",
							};
						} catch (err) {
							console.warn(`Failed to load image ${imageData.filename}:`, err);
							return {
								...imageData,
								src: null,
								isLoaded: false,
								error: err.message,
							};
						}
					});

					const batchResults = await Promise.allSettled(batchPromises);
					const processedBatch = batchResults.map((result, index) =>
						result.status === "fulfilled"
							? result.value
							: {
									...batch[index],
									src: null,
									isLoaded: false,
									error: "Failed to load",
								},
					);

					loadedImages.push(...processedBatch);
					setLoadingProgress((loadedImages.length / imagesToLoad.length) * 100);
				}

				setImages(loadedImages);
			} catch (err) {
				console.error("Error loading images:", err);
				setError("Failed to load gallery images. Please try again.");
			} finally {
				setLoading(false);
			}
		},
		[getFilteredMetadata],
	);

	// Load more images
	const loadMore = useCallback(async () => {
		if (loadingMore) return;

		setLoadingMore(true);
		const newCount = visibleCount + 12;
		setVisibleCount(newCount);

		try {
			await loadImages(selectedFilter, newCount);
		} catch (err) {
			console.error("Error loading more images:", err);
		} finally {
			setLoadingMore(false);
		}
	}, [loadingMore, visibleCount, selectedFilter, loadImages]);

	// Handle filter change
	const handleFilterChange = useCallback(
		(filter) => {
			if (filter === selectedFilter) return;

			setSelectedFilter(filter);
			setVisibleCount(12);
			loadImages(filter, 12);
		},
		[selectedFilter, loadImages],
	);

	// Initial load
	useEffect(() => {
		const initialLoad = async () => {
			try {
				setLoading(true);
				setError(null);
				setLoadingProgress(0);

				const metadata = getFilteredMetadata("all");
				const imagesToLoad = metadata.slice(0, 12);

				if (imagesToLoad.length === 0) {
					setImages([]);
					setLoading(false);
					return;
				}

				const loadedImages = [];
				const batchSize = 3;

				for (let i = 0; i < imagesToLoad.length; i += batchSize) {
					const batch = imagesToLoad.slice(i, i + batchSize);
					const batchPromises = batch.map(async (imageData) => {
						try {
							const result = await imageService.getImageWithMetadata(imageData);
							return {
								...result,
								id: result.id || imageData.id,
								description: result.description || imageData.description || "",
							};
						} catch (err) {
							console.warn(`Failed to load image ${imageData.filename}:`, err);
							return {
								...imageData,
								src: null,
								isLoaded: false,
								error: err.message,
							};
						}
					});

					const batchResults = await Promise.allSettled(batchPromises);
					const processedBatch = batchResults.map((result, index) =>
						result.status === "fulfilled"
							? result.value
							: {
									...batch[index],
									src: null,
									isLoaded: false,
									error: "Failed to load",
								},
					);

					loadedImages.push(...processedBatch);
					setLoadingProgress((loadedImages.length / imagesToLoad.length) * 100);
				}

				setImages(loadedImages);
			} catch (err) {
				console.error("Error loading images:", err);
				setError("Failed to load gallery images. Please try again.");
			} finally {
				setLoading(false);
			}
		};

		initialLoad();
	}, [getFilteredMetadata]); // Include getFilteredMetadata as dependency

	// Preload critical images on component mount
	useEffect(() => {
		const preloadCritical = async () => {
			const criticalImages = getFilteredMetadata("all")
				.slice(0, 6)
				.map((img) => img.filename);

			try {
				await imageService.preloadImages(criticalImages, { concurrency: 2 });
			} catch (err) {
				console.warn("Failed to preload some images:", err);
			}
		};

		preloadCritical();
	}, [getFilteredMetadata]);

	const currentMetadata = getFilteredMetadata(selectedFilter);
	const hasMore = images.length < currentMetadata.length;

	return (
		<PageContainer>
			<PageTitle>Our Recent Projects</PageTitle>

			<FilterButtons>
				<button
					type="button"
					className={selectedFilter === "all" ? "active" : ""}
					onClick={() => handleFilterChange("all")}
					disabled={loading}
				>
					See All Photos
				</button>
				<button
					type="button"
					className={selectedFilter === "residential" ? "active" : ""}
					onClick={() => handleFilterChange("residential")}
					disabled={loading}
				>
					Residential
				</button>
				<button
					type="button"
					className={selectedFilter === "commercial" ? "active" : ""}
					onClick={() => handleFilterChange("commercial")}
					disabled={loading}
				>
					Commercial
				</button>
			</FilterButtons>

			{/* Loading State */}
			{loading && (
				<LoadingIndicator>
					<div>
						<div>Loading gallery images...</div>
						<LoadingProgress>
							<LoadingBar progress={loadingProgress} />
						</LoadingProgress>
						<div>{Math.round(loadingProgress)}% complete</div>
					</div>
				</LoadingIndicator>
			)}

			{/* Error State */}
			{error && (
				<ErrorMessage>
					{error}
					<br />
					<button
						type="button"
						onClick={() => loadImages(selectedFilter, visibleCount)}
						style={{
							marginTop: "1rem",
							padding: "0.5rem 1rem",
							background: "#d32f2f",
							color: "white",
							border: "none",
							borderRadius: "4px",
							cursor: "pointer",
						}}
					>
						Try Again
					</button>
				</ErrorMessage>
			)}

			{/* Stats */}
			{!loading && !error && images.length > 0 && (
				<StatsContainer>
					<div>
						Showing {images.length} of {currentMetadata.length} images
					</div>
				</StatsContainer>
			)}

			{/* Gallery */}
			{!loading && !error && images.length > 0 && <Gallery images={images} />}

			{/* Load More Button */}
			{!loading && !error && hasMore && (
				<LoadMoreButton onClick={loadMore} disabled={loadingMore}>
					{loadingMore
						? "Loading..."
						: `Load More (${currentMetadata.length - images.length} remaining)`}
				</LoadMoreButton>
			)}

			{/* No Images Message */}
			{!loading && !error && images.length === 0 && (
				<LoadingIndicator>
					No images found for the selected category.
				</LoadingIndicator>
			)}
		</PageContainer>
	);
}

export default GalleryPage;
